import PropTypes from "prop-types";
import SEPA from "sepa";
// @mui
import {
  Button,
  FormGroup,
  Grid,
  Switch,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
// components
import { Controller, useForm } from "react-hook-form";
import {
  anredeArray,
  bankDataFields,
  bankDataOwnerField,
  contactDataFields,
  parentDataFields,
} from "../../../components/form/fields/inputFields";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import DEDateField from "../../../components/form/fields/DEDateField";
import { formatIBAN, getDateFromDateField } from "../../../utils/dataUtils";
import IBANTextField from "../../../components/form/fields/IBANTextField";
import { useEffect, useMemo } from "react";

// ----------------------------------------------------------------------

export default function NewContractForm({
  memberType,
  memberData,
  contractData,
  handleContractDataSubmit,
  isPublicRegistration,
}) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      lastName: memberData?.lastName || "",
      firstName: memberType === "child" ? "" : memberData?.firstName,
      isOwner: true,
      birthdate: new Date(),
      owner:
        memberType === "child"
          ? ""
          : memberData?.lastName + " " + memberData?.firstName,
      ...contractData,
      iban: contractData?.iban ? formatIBAN(contractData?.iban) : "",
      emailConfirm: contractData?.email || "",
    },
  });

  const isOwner = watch("isOwner");
  const lastName = watch("lastName");
  const firstName = watch("firstName");
  const emailValue = watch("email");
  const emailConfirmValue = watch("emailConfirm");
  const ownerName = `${lastName || ""} ${firstName || ""}`.trim();
  const normalizedEmail = String(emailValue || "").trim().toLowerCase();
  const normalizedEmailConfirm = String(emailConfirmValue || "")
    .trim()
    .toLowerCase();
  const hasEmailInput = Boolean(normalizedEmail || normalizedEmailConfirm);
  const emailsMatch =
    Boolean(normalizedEmail) && normalizedEmail === normalizedEmailConfirm;
  const showEmailSuccess =
    hasEmailInput && emailsMatch && !errors.email && !errors.emailConfirm;

  const requiredContactErrorByField = useMemo(
    () => ({
      street: "Bitte Straße und Hausnummer eingeben.",
      zip: "Bitte PLZ eingeben.",
      city: "Bitte Ort eingeben.",
      mobile: "Bitte Mobilnummer eingeben.",
      email: "Bitte eine gültige E-Mail eingeben.",
    }),
    [],
  );

  const contactFieldsWithRules = useMemo(
    () =>
      contactDataFields.map((field) => {
        const hasPublicRequirement = Object.prototype.hasOwnProperty.call(
          requiredContactErrorByField,
          field.value,
        );

        return {
          ...field,
          required: isPublicRegistration
            ? hasPublicRequirement
              ? requiredContactErrorByField[field.value]
              : false
            : field.required,
          errorMsg: isPublicRegistration
            ? hasPublicRequirement
              ? requiredContactErrorByField[field.value]
              : field.errorMsg
            : field.errorMsg,
        };
      }),
    [isPublicRegistration, requiredContactErrorByField],
  );

  const contactFieldsWithoutEmail = useMemo(
    () =>
      contactFieldsWithRules.filter((field) => field.value !== "email"),
    [contactFieldsWithRules],
  );

  useEffect(() => {
    if (isOwner === true && memberType === "child") {
      setValue("owner", ownerName);
    }
    if (isOwner === true && memberType !== "child") {
      setValue("owner", memberData.lastName + " " + memberData.firstName);
    }
    if (isOwner === false) {
      setValue("owner", "");
    }
  }, [isOwner, setValue, ownerName, memberType, memberData]);

  const onSubmit = (data) => {
    // Check if an IBAN was typed in and if so if its correct
    if (data.iban) {
      if (!SEPA.validateIBAN(data.iban)) {
        alert("Der eingegebene IBAN ist ungültig, bitte Eingabe prüfen.");
        return;
      }
      data.iban = data.iban.replace(/\s/g, "");
      data.iban = data.iban.toUpperCase();
    }

    const submitData = { ...data };
    delete submitData.emailConfirm;

    if (memberType !== "child") {
      submitData.birthdate = memberData.birthdate;
      submitData.title = memberData.title;
    }

    handleContractDataSubmit({
      ...submitData,
      birthdate: getDateFromDateField(submitData?.birthdate),
    });
  };

  const renderSiteInputFields = (fields) => {
    return fields.map((field) => {
      if (field.type === "date") {
        return (
          <Grid
            key={field.value + "-field"}
            item
            xs={12}
            md={field.size ? field.size : 4}
            sx={{ minHeight: 91 }}
          >
            <DEDateField
              control={control}
              name={field.value}
              label={field.label}
              required={field.required}
              errorMsg={field.errorMsg}
              helperText={field.helperText}
            />
          </Grid>
        );
      }

      if (field.value === "iban") {
        return (
          <Grid
            key={field.value + "-field"}
            item
            xs={12}
            md={field.size ? field.size : 4}
            sx={{ minHeight: 91 }}
          >
            <IBANTextField
              control={control}
              name={field.value}
              label={field.label}
              type={field.type}
              variant="outlined"
              required={field.required}
              fullWidth
              setError={setError}
              clearErrors={clearErrors}
            />
          </Grid>
        );
      }

      return (
        <Grid
          key={field.value + "-field"}
          item
          xs={12}
          md={field.size ? field.size : 4}
          sx={field?.minHeight ? { minHeight: 91 } : null}
        >
          <TextField
            control={control}
            name={field.value}
            label={field.label}
            type={field.value === "email" ? "email" : field.type}
            variant="outlined"
            required={field.required}
            errorMsg={field.errorMsg}
            helperText={field.helperText}
            fullWidth
            multiline={field?.multiline}
            rules={
              field.value === "email"
                ? {
                    pattern: {
                      value: emailRegex,
                      message: "Bitte eine gültige E-Mail eingeben.",
                    },
                  }
                : undefined
            }
          />
        </Grid>
      );
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ paddingTop: 5 }}
        >
          <Grid item xs={12}>
            <Typography variant="h5">Bankdaten</Typography>
          </Grid>
          <Grid item lg={12}>
            <FormGroup row>
              <label style={{ fontSize: "25px" }}>
                Ist{" "}
                {memberType === "child" ? "der Vertragsnehmer" : "das Mitglied"}{" "}
                der Kontoinhaber?
              </label>
              <label
                style={{
                  color: isOwner ? "grey" : "black",
                  marginLeft: 15,
                  marginTop: "5px",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                }}
              >
                Nein
              </label>
              <Controller
                name="isOwner"
                control={control}
                render={({ field }) => (
                  <Switch
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                )}
              />
              <label
                style={{
                  color: isOwner ? "black" : "grey",
                  marginTop: "5px",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                }}
              >
                Ja
              </label>
            </FormGroup>
            <Typography variant="body1">
              Wenn hier "Nein" ausgewählt wird, kann bei Inhaber ein vom
              Vertragsnehmer abweichender Beitragszahler eingegeben werden.
              <br />
              Beispiel: Großeltern zahlen für den Vertrag des Enkelkindes
            </Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ minHeight: 91 }}>
            <TextField
              control={control}
              name={bankDataOwnerField.value}
              label={bankDataOwnerField.label}
              type={bankDataOwnerField.type}
              variant="outlined"
              required={bankDataOwnerField.required}
              fullWidth
              disabled={isOwner}
            />
          </Grid>
          {renderSiteInputFields(bankDataFields)}
          {memberType === "child" && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">
                  Vertragsnehmer/Erziehungsberechtigter
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <SelectField
                  style={{ minWidth: 180 }}
                  control={control}
                  name="title"
                  value="title"
                  label="Anrede"
                  variant="outlined"
                  required
                  itemArray={anredeArray}
                />
              </Grid>
              {renderSiteInputFields(parentDataFields)}
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="h5">Kontaktdaten</Typography>
          </Grid>
          {renderSiteInputFields(contactFieldsWithoutEmail)}
          <Grid item xs={12} md={6}>
            <Controller
              name="email"
              control={control}
              rules={{
                required:
                  isPublicRegistration || contactDataFields.some((f) => f.value === "email" && f.required)
                    ? "Bitte eine gültige E-Mail eingeben."
                    : false,
                pattern: {
                  value: emailRegex,
                  message: "Bitte eine gültige E-Mail eingeben.",
                },
              }}
              render={({ field, fieldState }) => (
                <MuiTextField
                  {...field}
                  label="E-Mail"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required={Boolean(
                    isPublicRegistration ||
                      contactDataFields.some(
                        (entry) => entry.value === "email" && entry.required,
                      ),
                  )}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ||
                    (showEmailSuccess
                      ? "E-Mail stimmt überein."
                      : "Bitte eine gültige E-Mail eingeben.")
                  }
                  color={showEmailSuccess ? "success" : "primary"}
                  focused={showEmailSuccess}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="emailConfirm"
              control={control}
              rules={{
                required: "Bitte E-Mail bestätigen.",
                pattern: {
                  value: emailRegex,
                  message: "Bitte eine gültige E-Mail eingeben.",
                },
                validate: (value) =>
                  String(value || "").trim().toLowerCase() === normalizedEmail ||
                  "E-Mail stimmt nicht überein.",
              }}
              render={({ field, fieldState }) => (
                <MuiTextField
                  {...field}
                  label="E-Mail bestätigen"
                  type="email"
                  variant="outlined"
                  required
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ||
                    (showEmailSuccess
                      ? "E-Mail stimmt überein."
                      : "Bitte E-Mail erneut eingeben.")
                  }
                  color={showEmailSuccess ? "success" : "primary"}
                  focused={showEmailSuccess}
                />
              )}
            />
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          sx={{
            mt: 2,
            mb: 3,
            fontSize: "125%",
          }}
        >
          Weiter
        </Button>
      </form>
    </>
  );
}

NewContractForm.propTypes = {
  memberType: PropTypes.string,
  memberData: PropTypes.object,
  contractData: PropTypes.object,
  handleContractDataSubmit: PropTypes.func.isRequired,
  isPublicRegistration: PropTypes.bool,
};

NewContractForm.defaultProps = {
  memberType: "adult",
  memberData: {},
  contractData: {},
  isPublicRegistration: false,
};
